import React from 'react';
import { Button, Grid, Typography, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SidePanel, SidePanelContent, SidePanelFooter } from '@securspace/securspace-ui-kit';
import { formatCurrencyValue } from '../../../../../util/PaymentUtils';
import { useFeatureFlags } from '../../../../../hooks/routing/useFeatureFlags';
import type { Booking } from '../../../../../types/Booking';

const StepLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(1),
}));

const LineItem = styled(Grid)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const LineItemLabel = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.8rem',
  },
}));

const LineItemValue = styled(Typography)({
  fontSize: '0.875rem',
});

const TotalLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.875rem',
  color: theme.palette.text.primary,
}));

const TotalValue = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.875rem',
});

export interface CalculateCostObject {
  initialBookingPaymentProcessingFee: number;
  initialBookingChargeAmount: number | string;
  recurringBookingPaymentProcessingFee: number;
  recurringBookingChargeAmount: number;
  recurringBookingPaymentAmount: number;
  initialBookingPaymentAmount: number;
  firstRecurringPaymentDate: string;
  initialBookingTaxAmount: number;
  recurringBookingTaxAmount: number;
  lastRecurringPaymentDate: string;
}

interface ReviewPaymentPanelProps {
  open: boolean;
  calculateCostObject: CalculateCostObject | null;
  completeBooking: () => void;
  bookingToComplete: Booking;
  onClose: () => void;
}

const ReviewPaymentPanel = (props: ReviewPaymentPanelProps) => {
  const { calculateCostObject, bookingToComplete, completeBooking, open, onClose } = props;

  const taxAmountFlag = useFeatureFlags().collectTax;

  if (!calculateCostObject) return null;

  const {
    initialBookingPaymentProcessingFee,
    initialBookingChargeAmount,
    recurringBookingPaymentProcessingFee,
    recurringBookingChargeAmount,
    recurringBookingPaymentAmount,
    initialBookingPaymentAmount,
    firstRecurringPaymentDate,
    initialBookingTaxAmount,
    recurringBookingTaxAmount,
    lastRecurringPaymentDate,
  } = calculateCostObject;

  const totalNumberOfPayments = bookingToComplete.totalNumberOfPayments ?? 0;
  const recurringBooking = bookingToComplete.frequency === 'RECURRING';
  const durationTypeLabel = bookingToComplete.durationType === 'WEEKLY' ? 'Weekly' : 'Monthly';

  return (
    <SidePanel open={open} onClose={onClose} title="Review Payment Details">
      <SidePanelContent>
        <StepLabel variant="subtitle1">3. Review Payment Details</StepLabel>
        <Grid container flexDirection="column" gap={1} sx={{ mt: 1 }}>
          {initialBookingChargeAmount === 'Calculating....' ? (
            <Typography variant="body2" color="text.secondary">Calculating Payment Info...</Typography>
          ) : recurringBooking ? (
            <>
              <Grid container flexDirection="column" gap={0.5}>
                <LineItem container columnGap={2}>
                  <LineItemLabel>Initial Payment:</LineItemLabel>
                  <LineItemValue>
                    {initialBookingPaymentAmount === 0 ? '$0' : formatCurrencyValue(initialBookingPaymentAmount)}
                  </LineItemValue>
                </LineItem>
                <LineItem container columnGap={2}>
                  <LineItemLabel>Credit Card Processing Fee:</LineItemLabel>
                  <LineItemValue>
                    {initialBookingPaymentProcessingFee === 0 ? '$0' : formatCurrencyValue(initialBookingPaymentProcessingFee)}
                  </LineItemValue>
                </LineItem>
                {taxAmountFlag && (
                  <LineItem container columnGap={2}>
                    <LineItemLabel>Taxes:</LineItemLabel>
                    <LineItemValue>
                      {initialBookingTaxAmount === 0 ? '$0' : formatCurrencyValue(initialBookingTaxAmount)}
                    </LineItemValue>
                  </LineItem>
                )}
                <Divider sx={{ my: 0.5 }} />
                <LineItem container columnGap={2}>
                  <TotalLabel>
                    {totalNumberOfPayments > 0 ? 'Due Now:' : 'Total Cost:'}
                  </TotalLabel>
                  <TotalValue>
                    {initialBookingChargeAmount === 0 ? '$0' : formatCurrencyValue(initialBookingChargeAmount as number)}
                  </TotalValue>
                </LineItem>
              </Grid>

              {totalNumberOfPayments > 0 && (
                <Grid container flexDirection="column" gap={0.5} sx={{ mt: 2 }}>
                  <LineItem container columnGap={2}>
                    <LineItemLabel>Number Of Payments:</LineItemLabel>
                    <LineItemValue>{totalNumberOfPayments}</LineItemValue>
                  </LineItem>
                  <LineItem container columnGap={2}>
                    <LineItemLabel>First Payment Date:</LineItemLabel>
                    <LineItemValue>{firstRecurringPaymentDate}</LineItemValue>
                  </LineItem>
                  <LineItem container columnGap={2}>
                    <LineItemLabel>Last Payment Date:</LineItemLabel>
                    <LineItemValue>{lastRecurringPaymentDate}</LineItemValue>
                  </LineItem>

                  <Divider sx={{ my: 0.5 }} />

                  <LineItem container columnGap={2}>
                    <LineItemLabel>{`${durationTypeLabel} Payment:`}</LineItemLabel>
                    <LineItemValue>
                      {recurringBookingPaymentAmount === 0 ? '$0' : formatCurrencyValue(recurringBookingPaymentAmount)}
                    </LineItemValue>
                  </LineItem>
                  <LineItem container columnGap={2}>
                    <LineItemLabel>Credit Card Processing Fee:</LineItemLabel>
                    <LineItemValue>
                      {recurringBookingPaymentProcessingFee === 0 ? '$0' : formatCurrencyValue(recurringBookingPaymentProcessingFee)}
                    </LineItemValue>
                  </LineItem>
                  {taxAmountFlag && (
                    <LineItem container columnGap={2}>
                      <LineItemLabel>Taxes:</LineItemLabel>
                      <LineItemValue>
                        {recurringBookingTaxAmount === 0 ? '$0' : formatCurrencyValue(recurringBookingTaxAmount)}
                      </LineItemValue>
                    </LineItem>
                  )}
                  <Divider sx={{ my: 0.5 }} />
                  <LineItem container columnGap={2}>
                    <TotalLabel>{`Total ${durationTypeLabel} Cost:`}</TotalLabel>
                    <TotalValue>
                      {recurringBookingChargeAmount === 0 ? '$0' : formatCurrencyValue(recurringBookingChargeAmount)}
                    </TotalValue>
                  </LineItem>
                </Grid>
              )}
            </>
          ) : (
            <Grid container flexDirection="column" gap={0.5}>
              <LineItem container columnGap={2}>
                <LineItemLabel>One-Time Payment:</LineItemLabel>
                <LineItemValue>
                  {initialBookingPaymentAmount === 0 ? '$0' : formatCurrencyValue(initialBookingPaymentAmount)}
                </LineItemValue>
              </LineItem>
              <LineItem container columnGap={2}>
                <LineItemLabel>Credit Card Processing Fee:</LineItemLabel>
                <LineItemValue>
                  {initialBookingPaymentProcessingFee === 0 ? '$0' : formatCurrencyValue(initialBookingPaymentProcessingFee)}
                </LineItemValue>
              </LineItem>
              {taxAmountFlag && (
                <LineItem container columnGap={2}>
                  <LineItemLabel>Taxes:</LineItemLabel>
                  <LineItemValue>
                    {initialBookingTaxAmount === 0 ? '$0' : formatCurrencyValue(initialBookingTaxAmount)}
                  </LineItemValue>
                </LineItem>
              )}
              <Divider sx={{ my: 0.5 }} />
              <LineItem container columnGap={2}>
                <TotalLabel>Total Cost:</TotalLabel>
                <TotalValue>
                  {initialBookingChargeAmount === 0 ? '$0' : formatCurrencyValue(initialBookingChargeAmount as number)}
                </TotalValue>
              </LineItem>
            </Grid>
          )}
        </Grid>
      </SidePanelContent>
      <SidePanelFooter>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={4}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={onClose}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item xs={8}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={completeBooking}
              sx={{ py: 1.5, textTransform: 'none', fontSize: '1rem' }}
            >
              Complete Booking
            </Button>
          </Grid>
        </Grid>
      </SidePanelFooter>
    </SidePanel>
  );
};

export default ReviewPaymentPanel;
